import cn from 'classnames';
import FocusTrap from 'focus-trap-react';
import { Field } from 'formik';
import isEqual from 'lodash/isEqual';
import { useState, useRef, createRef, useEffect } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import Error from 'components/common/forms/Error';
import Tooltip from 'components/common/Tooltip';
import ICircle from 'components/icons/ICircle';
import CaretDown from 'components/icons/navigation/CaretDown';
import { TEMPO } from 'context/BrandContext';
import formLabel from 'helpers/formLabel';
import useBrand from 'hooks/useBrand';
import useClickOutside from 'hooks/useClickOutside';

const serializeToListOfObj = optionArray => {
  return optionArray.map(option => {
    return typeof option === 'object'
      ? { ...option, key: option.key.toString() }
      : { key: option.toString(), value: option };
  });
};

const NO_GROUP = 'noGroup';

const serializeToObj = optionArray => {
  return optionArray.reduce((acc, option) => {
    const key = option.group || NO_GROUP;
    if (!acc[key]) {
      acc[key] = [{ key: option.key, value: option.value, disabled: option.disabled }];
    } else {
      acc[key].push({ key: option.key, value: option.value, disabled: option.disabled });
    }
    return acc;
  }, {});
};

export const CustomDropdown = ({
  changeHandler,
  disabled,
  error,
  field,
  name,
  onBlur,
  options,
  optionButtonClass,
  optionsContainerClass,
  selectContainerClass,
  selectedFieldButtonClass,
  value,
  variant,
}) => {
  const isTempo = useBrand(TEMPO);
  const isRefreshed = variant === 'refreshed';
  const dropdownRef = useRef(null);
  const selectedOptionRef = useRef(null);
  const optionRefs = useRef(options.map(() => createRef()));
  const transitionRef = useRef();
  const timerRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentlyFocusedOption, setCurrentlyFocusedOption] = useState(0);
  const [triggerInitialFocus, setTriggerInitialFocus] = useState(false);
  const [numberOrLetterPressed, setNumberOrLetterPressed] = useState({ key: '' });
  const serializedOptions = serializeToListOfObj(options || []);
  const serializedOptionsObj = serializeToObj(serializedOptions);
  const selectedOption = value || field?.value;
  const selectedOptionKey = selectedOption
    ? serializedOptions.find(option => isEqual(option.value, selectedOption))?.key || ''
    : '';
  const dropdownName = name || field?.name;
  let count = 0;

  const focusOnGivenKey = givenKey => {
    const firstOptionToMatchIdx = optionRefs.current.findIndex(ref =>
      ref.current?.innerText?.toLowerCase().startsWith(givenKey)
    );

    const optionFound = firstOptionToMatchIdx > -1;

    if (optionFound) {
      optionRefs.current[firstOptionToMatchIdx].current.focus();
      setCurrentlyFocusedOption(firstOptionToMatchIdx);
    }

    return optionFound;
  };

  useEffect(() => {
    // If the dropdown was just opened, focus on the currently selected option or
    // the first one if not found.
    if (
      isDropdownOpen &&
      optionRefs.current &&
      currentlyFocusedOption === 0 &&
      triggerInitialFocus
    ) {
      const optionFound = selectedOptionKey
        ? focusOnGivenKey(selectedOptionKey.toLowerCase())
        : false;
      if (!optionFound) optionRefs.current[currentlyFocusedOption].current.focus();

      setTriggerInitialFocus(false);
    }
  }, [isDropdownOpen, optionRefs.current, triggerInitialFocus]);

  const onDropdownClose = () => {
    setIsDropdownOpen(false);
    setCurrentlyFocusedOption(0);
  };

  useEffect(() => {
    // If a number or letter is pressed, and the dropdown is open, focus on the first option that
    // starts with that number or letter. If none are found, keep the current state.
    if (isDropdownOpen && numberOrLetterPressed.navToKey) {
      const optionFound = focusOnGivenKey(numberOrLetterPressed.key);
      if (!optionFound && !numberOrLetterPressed.dropdownAlreadyOpen) onDropdownClose();

      setNumberOrLetterPressed({ ...numberOrLetterPressed, navToKey: false });
      timerRef.current = setTimeout(() => {
        setNumberOrLetterPressed({ key: '' });
      }, 1000);
    }
  }, [isDropdownOpen, numberOrLetterPressed]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  useClickOutside(dropdownRef, onDropdownClose);

  const getEventType = event => {
    const numberPressed = event.keyCode >= 48 && event.keyCode <= 57;
    const letterPressed = event.keyCode >= 65 && event.keyCode <= 90;
    const isUpArrow = event.key === 'ArrowUp';
    const isDownArrow = event.key === 'ArrowDown';
    const isTab = event.key === 'Tab' && !event.shiftKey;
    const isShiftTab = event.key === 'Tab' && event.shiftKey;
    const isEnter = event.key === 'Enter';
    const isEscape = event.key === 'Escape';

    return {
      numberPressed,
      letterPressed,
      isUpArrow,
      isDownArrow,
      isTab,
      isShiftTab,
      isEnter,
      isEscape,
    };
  };

  const onNumberOrLetterPressed = (event, dropdownAlreadyOpen) => {
    let key = event.key.toLowerCase();
    if (numberOrLetterPressed.key.length === 1) key = `${numberOrLetterPressed.key}${key}`;

    setNumberOrLetterPressed({
      key,
      dropdownAlreadyOpen,
      navToKey: true,
    });
    if (!dropdownAlreadyOpen) setIsDropdownOpen(true);
  };

  // Handles keyboard navigation when the focus is on the selected option.
  // If pressing the up or down arrow, or enter, when the dropdown is closed, the dropdown should
  // open and the currently selected option in the dropdown should focus.
  // If tabbing or pressing the down arrow when the dropdown is already open, the focus should
  // move to the first option in the dropdown.
  // If shift + tabbing or pressing the up arrow when the dropdown is already open, the focus
  // should move to the last option in the dropdown.
  // If a number or letter is pressed, the dropdown should open and the focus should move to the
  // first option that starts with that number or letter. If no option is found, nothing happens.
  const handleSelectionKeyDown = event => {
    const {
      numberPressed,
      letterPressed,
      isUpArrow,
      isDownArrow,
      isTab,
      isShiftTab,
      isEnter,
      isEscape,
    } = getEventType(event);

    if (isEscape && isDropdownOpen) return onDropdownClose();

    if (numberPressed || letterPressed) return onNumberOrLetterPressed(event, isDropdownOpen);

    if (numberOrLetterPressed.key) setNumberOrLetterPressed({ key: '' });

    if (!isDropdownOpen && (isShiftTab || isTab)) return;

    if (!isDropdownOpen && (isUpArrow || isDownArrow || isEnter)) {
      event.preventDefault();
      setTriggerInitialFocus(true);
      return setIsDropdownOpen(true);
    }

    let newOption = null;
    if (isUpArrow || isShiftTab) {
      event.preventDefault();
      newOption = serializedOptions.length - 1;
    } else if (isDownArrow || isTab) {
      event.preventDefault();
      newOption = 0;
    }

    if (newOption === null) return;
    optionRefs.current[newOption].current.focus();
    setCurrentlyFocusedOption(newOption);
  };

  // Handles keyboard navigation when the dropdown is open, and the focus is on one of the options.
  // If tabbing or pressing the down arrow, the focus should move down the options container.
  // If shift + tabbing or pressing the up arrow, the focus should move up the options container.
  // If a number or letter is pressed, the focus should move to the first option that starts with
  // that number or letter. If no option is found, nothing happens.
  const handleOptionKeyDown = event => {
    const { numberPressed, letterPressed, isUpArrow, isDownArrow, isTab, isShiftTab, isEscape } =
      getEventType(event);

    if (isEscape) return onDropdownClose();

    if (numberPressed || letterPressed) return onNumberOrLetterPressed(event, true);

    if (numberOrLetterPressed.key) setNumberOrLetterPressed({ key: '' });

    let newOption = null;
    if (isUpArrow || isShiftTab) {
      event.preventDefault();
      if (currentlyFocusedOption === 0) {
        selectedOptionRef.current.focus();
        return setCurrentlyFocusedOption(null);
      }

      newOption = currentlyFocusedOption - 1;
    } else if (isDownArrow || isTab) {
      event.preventDefault();
      if (currentlyFocusedOption === serializedOptions.length - 1) {
        selectedOptionRef.current.focus();
        return setCurrentlyFocusedOption(null);
      }

      newOption = currentlyFocusedOption + 1;
    }

    if (newOption === null) return;
    optionRefs.current[newOption].current.focus();
    setCurrentlyFocusedOption(newOption);
  };

  const onOptionChange = (value, e) => {
    e.preventDefault();
    changeHandler(value);
    onDropdownClose();
  };

  const onDropdownSelectorClick = () => {
    if (isDropdownOpen) return onDropdownClose();

    setIsDropdownOpen(true);
    setTriggerInitialFocus(true);
  };

  return (
    <div className={selectContainerClass} id={dropdownName} ref={dropdownRef}>
      <FocusTrap active={isDropdownOpen}>
        <div className="relative">
          <button
            onClick={onDropdownSelectorClick}
            disabled={disabled}
            className={cn(
              'text flex h-[52px] w-full cursor-pointer appearance-none items-center justify-between border border-solid bg-white-100 leading-tight focus:outline-none focus:ring disabled:cursor-not-allowed',
              isRefreshed
                ? cn('rounded-2xl px-6 py-4', {
                    'border-cream-120 focus:border-green-100': !isTempo,
                    'border-charcoal-60 focus:border-tempo-blue-90': isTempo,
                  })
                : cn('rounded px-4 py-3', {
                    'border-charcoal-80': !error,
                  }),
              {
                'hover:border-green-100': !isTempo && !disabled && !error,
                'hover:text-green-100 focus:text-green-100 focus:ring-green-70':
                  !isTempo && !disabled,
                'hover:border-tempo-navy-90': isTempo && !disabled && !error,
                'hover:text-tempo-blue-90 focus:text-tempo-blue-90 focus:ring-tempo-blue-70':
                  isTempo && !disabled,
                'border-red-100': error && !isTempo && !disabled,
                'border-red-120': error && isTempo && !disabled,
                'text-charcoal-80': disabled,
              },
              selectedFieldButtonClass
            )}
            aria-controls={`${dropdownName}-dropdown-list`}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            type="button"
            ref={selectedOptionRef}
            onKeyDown={handleSelectionKeyDown}
            onBlur={onBlur}
            data-testid={`${dropdownName}-toggle-button`}
          >
            <span
              className={cn(
                'mr-5 overflow-hidden whitespace-nowrap !text-charcoal-100',
                disabled && '!text-charcoal-80'
              )}
            >
              {selectedOptionKey}
            </span>
            <CaretDown
              className={cn('iconMd pointer-events-none absolute right-3 transition-all', {
                'rotate-180 transform': isDropdownOpen,
              })}
            />
          </button>
          <TransitionGroup className="w-full">
            {isDropdownOpen && (
              <CSSTransition timeout={200} classNames="fadeInOut" nodeRef={transitionRef}>
                <div
                  ref={transitionRef}
                  className={cn(
                    'absolute top-[56px] z-one flex max-h-80 w-full flex-col overflow-auto rounded-b bg-white-100 px-1.5 py-2 shadow-lg ring-1 ring-charcoal-50',
                    optionsContainerClass
                  )}
                  id={`${dropdownName}-dropdown-list`}
                  role="combobox"
                  aria-labelledby={dropdownName}
                  aria-activedescendant={
                    currentlyFocusedOption !== null &&
                    `${dropdownName}-option-${currentlyFocusedOption}`
                  }
                >
                  {Object.keys(serializedOptionsObj).map((optionKey, idx) => {
                    const noGroupKey = optionKey === NO_GROUP;

                    return (
                      <div
                        className="flex h-full w-full flex-col"
                        {...(!noGroupKey ? { role: 'group', 'aria-label': optionKey } : {})}
                        key={idx}
                      >
                        {!noGroupKey && (
                          <span className="text pb-1 text-start font-medium text-charcoal-100">
                            {optionKey}
                          </span>
                        )}
                        {serializedOptionsObj[optionKey].map(option => {
                          const idxKey = count++;

                          return (
                            <button
                              className={cn(
                                'text text-secondary mb-1 rounded px-0.5 text-start outline-none',
                                {
                                  '!mb-0': idxKey === serializedOptions.length - 1,
                                  'ml-2': !noGroupKey,
                                  'pointer-events-none text-charcoal-80': option.disabled,
                                  'hover:bg-charcoal-50 focus:bg-charcoal-50': !option.disabled,
                                },
                                optionButtonClass
                              )}
                              onClick={e => onOptionChange(option.value, e)}
                              key={idxKey}
                              role="option"
                              disabled={option.disabled || false}
                              ref={optionRefs.current[idxKey]}
                              onKeyDown={handleOptionKeyDown}
                              id={`${dropdownName}-option-${idxKey}`}
                              data-testid={`${dropdownName}-option-${idxKey}`}
                            >
                              <span className="flex h-full min-h-7">{option.key}</span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </CSSTransition>
            )}
          </TransitionGroup>
        </div>
      </FocusTrap>
    </div>
  );
};

CustomDropdown.propTypes = {
  changeHandler: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  field: PropTypes.object,
  name: PropTypes.string,
  onBlur: PropTypes.func,
  options: PropTypes.array.isRequired,
  optionButtonClass: PropTypes.string,
  optionsContainerClass: PropTypes.string,
  selectContainerClass: PropTypes.string,
  selectedFieldButtonClass: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  variant: PropTypes.oneOf(['refreshed']),
};

const SelectField = ({
  containerClass = '',
  disabled = false,
  error = '',
  formikField = false,
  helperText = '',
  hideHelperErrorContainer = false,
  label = '',
  labelClass = '',
  labelTooltip = '',
  onBlur = () => {},
  optionButtonClass = '',
  optionsContainerClass = '',
  selectContainerClass = '',
  selectedFieldButtonClass = '',
  value = '',
  changeHandler,
  name,
  onOpenModal,
  options,
}) => {
  const isTempo = useBrand(TEMPO);
  const helperRef = useRef();
  const errorRef = useRef();

  return (
    <div className={containerClass}>
      <label htmlFor={name} className={cn('textMd mb-2 leading-tight', labelClass)}>
        {label || formLabel(name)}
        {labelTooltip && (
          <Tooltip ariaId={name} containerClass="ml-1">
            <p className="text-charcoal-90">{labelTooltip}</p>
          </Tooltip>
        )}
        {onOpenModal != null && (
          <span>
            &nbsp;
            <button onClick={onOpenModal} type="button">
              <ICircle containerClass="ml-1 text-charcoal-90 mb-0.5 h-4 w-4 align-middle" />
            </button>
          </span>
        )}
      </label>
      <div
        className={cn('w-full', {
          'mb-1': Boolean(error) || Boolean(helperText),
          tempo: isTempo,
        })}
      >
        {formikField ? (
          <Field
            changeHandler={changeHandler}
            component={CustomDropdown}
            disabled={disabled}
            error={error}
            id={name}
            name={name}
            onBlur={onBlur}
            options={options}
            optionButtonClass={optionButtonClass}
            optionsContainerClass={optionsContainerClass}
            selectContainerClass={selectContainerClass}
            selectedFieldButtonClass={selectedFieldButtonClass}
            value={value}
          />
        ) : (
          <CustomDropdown
            changeHandler={changeHandler}
            disabled={disabled}
            error={error}
            name={name}
            onBlur={onBlur}
            options={options}
            optionButtonClass={optionButtonClass}
            optionsContainerClass={optionsContainerClass}
            selectContainerClass={selectContainerClass}
            selectedFieldButtonClass={selectedFieldButtonClass}
            value={value}
          />
        )}
      </div>
      {!hideHelperErrorContainer && (
        <TransitionGroup className="crossfadeContainer textSm mt-1 min-h-6">
          {Boolean(helperText) && (
            <CSSTransition
              in={Boolean(helperText)}
              timeout={200}
              classNames="fadeInOut"
              nodeRef={helperRef}
            >
              <p ref={helperRef} className="textSm font-normal text-charcoal-90">
                {helperText}
              </p>
            </CSSTransition>
          )}
          {Boolean(error) && (
            <CSSTransition
              in={Boolean(error)}
              timeout={200}
              classNames="fadeInOut"
              nodeRef={errorRef}
            >
              <div ref={errorRef}>
                <Error message={error} className="crossfadeItem bg-white-100" />
              </div>
            </CSSTransition>
          )}
        </TransitionGroup>
      )}
    </div>
  );
};

SelectField.propTypes = {
  containerClass: PropTypes.string,
  labelClass: PropTypes.string,
  labelTooltip: PropTypes.string,
  onOpenModal: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        key: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
        group: PropTypes.string,
      }),
    ])
  ).isRequired,
  changeHandler: PropTypes.func.isRequired,
  formikField: PropTypes.bool,
  disabled: PropTypes.bool,
  selectContainerClass: PropTypes.string,
  selectedFieldButtonClass: PropTypes.string,
  optionsContainerClass: PropTypes.string,
  optionButtonClass: PropTypes.string,
  onBlur: PropTypes.func,
  hideHelperErrorContainer: PropTypes.bool,
};

export default SelectField;
